import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { initializeFromItem } from '@sls-react/store/listFlow/initializationActions';
import { RootState } from '@sls-react/store/rootReducer';

export interface ListFlowPricingState {
  acceptOffers: boolean;
  isAuction?: boolean;
  listPrice?: number;
  retailPrice?: number;

  autoPriceDrop: {
    enabled: boolean;
    minimum: number;
  };
}

export const listFlowPricingInitialState: ListFlowPricingState = {
  acceptOffers: true,
  autoPriceDrop: {
    enabled: false,
    minimum: 0,
  },
};

const listFlowPricingSlice = createSlice({
  initialState: listFlowPricingInitialState,
  name: 'listFlow/pricing',
  reducers: {
    changePricing(
      state,
      action: PayloadAction<{
        field: string;
        value: number;
      }>,
    ) {
      const { field, value } = action.payload;
      state[field] = value;
    },
    changedAutoPriceDrop(
      state,
      action: PayloadAction<{ enabled?: boolean; newMinimum?: number }>,
    ) {
      if (action.payload.enabled != null) {
        state.autoPriceDrop.enabled = action.payload.enabled;
      }
      if (action.payload.newMinimum != null) {
        state.autoPriceDrop.minimum = action.payload.newMinimum;
      }

      if (
        state.autoPriceDrop.enabled &&
        !state.autoPriceDrop.minimum &&
        state.listPrice
      ) {
        state.autoPriceDrop.minimum = Math.round(state.listPrice / 2);
      }
    },
    toggleAcceptsOffers(state, action: PayloadAction<{ enabled: boolean }>) {
      state.acceptOffers = action.payload.enabled;
    },
    toggleAuction(state, action: PayloadAction<{ enabled: boolean }>) {
      const enabled = action.payload.enabled;
      state.isAuction = enabled;

      if (enabled) {
        state.acceptOffers = true;
      }
    },
  },
  extraReducers: builder => {
    //! HEY, if you're editing this reducer, check if you need to edit `getListFlowSubmittableItem`
    builder.addCase(initializeFromItem, (state, action) => {
      const item = action.payload;
      const newState: ListFlowPricingState = {
        listPrice: item.list_price,
        isAuction: item.auction,
        retailPrice: item.price_retail,
        acceptOffers: item.accepts_offers != null ? item.accepts_offers : true,
        autoPriceDrop: {
          enabled: !!item.auto_price_drop,
          minimum: item.auto_price_drop_min_price || 0,
        },
      };

      return newState;
    });
  },
});

export const {
  changePricing,
  changedAutoPriceDrop,
  toggleAcceptsOffers,
  toggleAuction,
} = listFlowPricingSlice.actions;

const listFlowPricingReducer = listFlowPricingSlice.reducer;

export default listFlowPricingReducer;

export const getListFlowPricing = (state: RootState) => state.listFlow.pricing;
export const getListFlowAutoPriceDrop = (state: RootState) =>
  state.listFlow.pricing.autoPriceDrop;
export const getListFlowAcceptsOffers = (state: RootState) =>
  state.listFlow.pricing.acceptOffers;
